import React, { useEffect, useRef, useState } from "react";

// Loaded on demand so pdf.js and the HEIC decoder stay out of the main bundle.
const loadPdf = () => import("../helpers/pdfRenderer");
const loadHeic = () => import("../helpers/heicConverter");

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const CONVERT_TIMEOUT_MS = 45000;

// "pdf" | "image" | "heic" | null (null = the browser cannot show it inline, so it is downloaded as before).
export const viewerKind = (doc) => {
  const type = (doc?.fileType || "").toLowerCase();
  const ext = ((doc?.name || "").split(".").pop() || "").toLowerCase();
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (type.includes("heic") || type.includes("heif") || ext === "heic" || ext === "heif") return "heic";
  if (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  return null;
};

const mimeFor = (kind, doc) => {
  if (kind === "pdf") return "application/pdf";
  const type = (doc?.fileType || "").toLowerCase();
  if (type.startsWith("image/")) return type;
  if (kind === "heic") return "image/heic";
  const ext = ((doc?.name || "").split(".").pop() || "").toLowerCase();
  return `image/${ext === "jpg" ? "jpeg" : ext || "jpeg"}`;
};

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("preview timed out")), ms))]);

// Reads the body while reporting progress (0-100) when the size is known.
async function readWithProgress(resp, onProgress) {
  const total = Number(resp.headers?.get?.("content-length")) || 0;
  if (!resp.body?.getReader || !total) return resp.arrayBuffer();
  const reader = resp.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(Math.min(99, Math.round((received / total) * 100)));
  }
  const all = new Uint8Array(received);
  let offset = 0;
  chunks.forEach((c) => {
    all.set(c, offset);
    offset += c.length;
  });
  return all.buffer;
}

// What the client sees while waiting; `progress` (0-100) makes the bar determinate.
const stageText = ({ stage, progress, page, pages }) => {
  switch (stage) {
    case "download":
      return progress == null ? "Baixando documento..." : `Baixando documento... ${progress}%`;
    case "prepare":
      return "Preparando visualização...";
    case "convert":
      return "Convertendo a foto para visualização...";
    case "render":
      return pages ? `Carregando página ${page} de ${pages}...` : "Carregando páginas...";
    default:
      return "Abrindo documento...";
  }
};

// Docrio serves every file as application/octet-stream, which makes browsers download it. We fetch the
// bytes ourselves (the bucket allows CORS) and show them with the right type instead (TDDS-3494).
// `url` may arrive after the viewer opens (the signed URL is requested on click).
export default function DocumentViewer({ doc, url, onClose }) {
  const kind = viewerKind(doc);
  const name = doc?.name || "Documento";
  const [state, setState] = useState({ status: "loading", objectUrl: "", displayUrl: "", note: "" });
  const [progress, setProgress] = useState({ stage: "open" });
  const pagesRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  // Fetch the renderer in parallel with the document instead of after it.
  useEffect(() => {
    if (kind === "pdf") loadPdf().then((m) => m.preloadPdf()).catch(() => {});
    if (kind === "heic") loadHeic().then((m) => m.preloadHeic()).catch(() => {});
  }, [kind]);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    let objectUrl = "";
    let convertedUrl = "";
    (async () => {
      try {
        setProgress({ stage: "download" });
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`download failed: ${resp.status}`);
        const buffer = await readWithProgress(resp, (p) => !cancelled && setProgress({ stage: "download", progress: p }));
        if (cancelled) return;
        const blob = new Blob([buffer], { type: mimeFor(kind, doc) });
        objectUrl = URL.createObjectURL(blob);
        if (kind === "pdf") {
          setProgress({ stage: "prepare" });
          const { renderPdf } = await loadPdf();
          if (cancelled) return;
          setState({ status: "rendering", objectUrl, displayUrl: "", note: "" });
          const { pages, rendered } = await renderPdf(new Uint8Array(buffer), pagesRef.current, {
            isCancelled: () => cancelled,
            onPage: (page, total) => !cancelled && setProgress({ stage: "render", page, pages: total, progress: Math.round(((page - 1) / total) * 100) }),
          });
          if (cancelled) return;
          setState({
            status: "ready",
            objectUrl,
            displayUrl: "",
            note:
              rendered < pages
                ? `Mostrando ${rendered} de ${pages} páginas. Baixe o arquivo para ver o documento completo.`
                : "",
          });
        } else if (kind === "heic") {
          // "Baixar" keeps the original HEIC; only the preview is converted when the browser can't show it.
          setProgress({ stage: "prepare" });
          const heic = await loadHeic();
          let displayUrl = objectUrl;
          if (!(await heic.canDisplayNatively(objectUrl))) {
            if (cancelled) return;
            setProgress({ stage: "convert" });
            const jpeg = await withTimeout(heic.heicToJpeg(blob), CONVERT_TIMEOUT_MS);
            if (cancelled) return;
            convertedUrl = URL.createObjectURL(jpeg);
            displayUrl = convertedUrl;
          }
          if (cancelled) return;
          setState({ status: "ready", objectUrl, displayUrl, note: "" });
        } else {
          setState({ status: "ready", objectUrl, displayUrl: objectUrl, note: "" });
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to preview document:", err);
        setState({ status: "error", objectUrl, displayUrl: "", note: "" });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (convertedUrl) URL.revokeObjectURL(convertedUrl);
    };
  }, [url, kind, doc]);

  const downloadHref = state.objectUrl || url;
  const working = state.status === "loading" || state.status === "rendering";

  return (
    <div className="docsViewerOverlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="docsViewer" role="dialog" aria-modal="true" aria-label={`Visualizar ${name}`} aria-busy={working}>
        <div className="docsViewerHeader">
          <div className="docsViewerTitle" title={name}>
            {name}
          </div>
          <div className="docsViewerActions">
            {downloadHref ? (
              <a className="docsAction docsAction--ghost" href={downloadHref} download={name}>
                Baixar
              </a>
            ) : null}
            <button
              type="button"
              className="docsAction"
              ref={closeRef}
              onClick={onClose}
              aria-label="Fechar visualização"
            >
              Fechar
            </button>
          </div>
        </div>
        {working ? (
          <div
            className={`docsViewerProgress${progress.progress == null ? " is-indeterminate" : ""}`}
            role="progressbar"
            aria-label={stageText(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.progress ?? undefined}
          >
            <div className="docsViewerProgressBar" style={progress.progress == null ? undefined : { width: `${progress.progress}%` }} />
          </div>
        ) : null}
        <div className="docsViewerBody">
          {working ? (
            <div className="docsViewerLoading" role="status">
              <span className="docsSpinner" aria-hidden="true" />
              <span>{stageText(progress)}</span>
            </div>
          ) : null}
          {state.status === "error" ? (
            <div className="docsViewerStatus" role="alert">
              Não foi possível mostrar este documento aqui. Use “Baixar” para abrir o arquivo.
            </div>
          ) : null}
          {state.note ? <div className="docsMuted docsViewerStatus">{state.note}</div> : null}
          {(kind === "image" || kind === "heic") && state.status === "ready" ? (
            <img className="docsViewerImage" src={state.displayUrl} alt={name} />
          ) : null}
          {kind === "pdf" ? <div className="docsViewerPages" ref={pagesRef} /> : null}
        </div>
      </div>
    </div>
  );
}
